import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { UserLoggedInGuard, UserNotLoggedInGuard, cartGuard, featureGuard } from './shared/guards';
import { ShellUser, ShellSales, ShellAuth } from './shells';
import { shippingAddressesCleanup, shippingAddressesLoaded } from './store/shipping-addresses';
import { productsCleanup } from './store/products';
import { productsPricingCleanup } from './store/product-pricing';
import { productCleanup } from './store/product';
import { shoppingCartCleanup, shoppingCartLoaded } from './store/shopping-cart';
import { favouritesCleanup, favouritesLoaded } from './store/favourites';
import { productCategoriesCleanup } from './store/product-categories';
import { productsSubstitutesCleanup } from './store/product-substitutes';
import { concessionsCleanup } from './store/concessions/guards/cleanup.guard';
import { userLoaded } from './store/user';

// #FIXME - remove after multiproject setup
import { adminGuard } from './shared/guards/admin/admin.guard';
import { panelGuard } from './shared/guards/admin/panel.guard';
import { cleanupBlockades } from './modules/blockades/guards/cleanup.guard';
import { cleanupAddressAlert } from './products/guards/cleanup.guard';
import { addressAlertGuard } from './products/guards/address-alert.guard';
import { userChecksLoaded } from './store/user-checks/guards/loaded.guard';
import { userChecksCleanup } from './store/user-checks/guards/cleanup.guard';
import {
    pushNotificationsCleanup,
    pushNotificationsDevicesLoaded,
    pushNotificationsDevicesRegister,
    pushNotificationsDevicesUnregister,
    pushNotificationsRegister,
    pushNotificationsStateLoaded,
    pushNotificationsUnregister,
} from './store/push-notifications';

const routes: Routes = [
    {
        path: 'admin',
        canActivate: [adminGuard()],
        loadChildren: () => import('./admin/admin.module').then((m) => m.AdminModule),
    },
    {
        path: '',
        canActivate: [panelGuard()],
        children: [
            {
                path: '',
                canActivateChild: [userLoaded(), pushNotificationsStateLoaded()],
                children: [
                    {
                        path: '',
                        redirectTo: 'dashboard',
                        pathMatch: 'full',
                    },
                    {
                        path: '',
                        canActivate: [UserNotLoggedInGuard],
                        children: [
                            ShellAuth.childRoutes([
                                {
                                    path: 'auth',
                                    loadChildren: () => import('./auth/auth.module').then((m) => m.AuthModule),
                                },
                            ]),
                        ],
                    },
                    {
                        path: '',
                        canActivate: [UserLoggedInGuard, pushNotificationsRegister(), pushNotificationsDevicesLoaded()],
                        canDeactivate: [pushNotificationsUnregister(), pushNotificationsCleanup()],
                        children: [
                            {
                                path: '',
                                canActivate: [pushNotificationsDevicesRegister()],
                                canDeactivate: [pushNotificationsDevicesUnregister()],
                                children: [
                                    ShellUser.childRoutes([
                                        {
                                            path: '',
                                            canActivate: [shippingAddressesLoaded()],
                                            canDeactivate: [shippingAddressesCleanup()],
                                            children: [
                                                {
                                                    path: '',
                                                    canActivate: [shoppingCartLoaded(), favouritesLoaded()],
                                                    canDeactivate: [
                                                        productsCleanup(),
                                                        productCleanup(),
                                                        productsPricingCleanup(),
                                                        productsSubstitutesCleanup(),
                                                        favouritesCleanup(),
                                                        shoppingCartCleanup(),
                                                        productCategoriesCleanup(),
                                                        concessionsCleanup(),
                                                        cleanupBlockades(),
                                                        cleanupAddressAlert(),
                                                        userChecksCleanup(),
                                                    ],
                                                    children: [
                                                        {
                                                            path: 'dashboard',
                                                            loadChildren: () => import('./dashboard/dashboard.module').then((m) => m.DashboardModule),
                                                        },
                                                        {
                                                            path: 'products',
                                                            canActivate: [addressAlertGuard(), userChecksLoaded()],
                                                            loadChildren: () => import('./products/products.module').then((m) => m.ProductsModule),
                                                        },
                                                        {
                                                            path: 'categories',
                                                            loadChildren: () => import('./categories/categories.module').then((m) => m.CategoriesModule),
                                                            canActivate: [featureGuard('nav.categories')],
                                                        },
                                                        {
                                                            path: 'favourites',
                                                            loadChildren: () => import('./favourites/favourites.module').then((m) => m.FavouritesModule),
                                                        },
                                                        {
                                                            path: 'cart',
                                                            canActivate: [cartGuard(), userChecksLoaded()],
                                                            loadChildren: () => import('./cart/cart.module').then((m) => m.CartModule),
                                                        },
                                                        {
                                                            path: 'temp',
                                                            loadChildren: () => import('./temp/temp.module').then((m) => m.TempModule),
                                                        },
                                                        {
                                                            path: 'user-profile',
                                                            loadChildren: () => import('./profile/profile.module').then((m) => m.ProfileModule),
                                                        },
                                                        {
                                                            path: 'documents',
                                                            loadChildren: () => import('./documents/documents.module').then((m) => m.DocumentsModule),
                                                        },
                                                    ],
                                                },
                                                {
                                                    path: 'complaints',
                                                    loadChildren: () => import('./complaints/complaints.module').then((m) => m.ComplaintsModule),
                                                },
                                            ],
                                        },
                                    ]),
                                    ShellSales.childRoutes([
                                        {
                                            path: 'customers',
                                            loadChildren: () => import('./customers/customers.module').then((m) => m.CustomersModule),
                                        },
                                        {
                                            path: 'profile',
                                            loadChildren: () => import('./profile/profile.module').then((m) => m.ProfileModule),
                                        },
                                    ]),
                                ],
                            },
                            {
                                path: 'logout',
                                loadChildren: () => import('./logout/logout.module').then((m) => m.LogoutModule),
                            },
                        ],
                    },
                ],
            },
        ],
    },
    {
        path: '**',
        redirectTo: 'dashboard',
        pathMatch: 'full',
    },
];

@NgModule({
    imports: [RouterModule.forRoot(routes, { enableTracing: false })],
    exports: [RouterModule],
    providers: [],
})
export class AppRoutingModule {}
